import { PAYMENT_GATEWAY_MAP } from "@shared/constants/enums";
import { PaymentServiceBasket } from "../../services/useBasketPaymentService";

/**
 * Checks if a basket's payment_gateway can be considered to be of type Stripe
 */
export function isStripeBasketGateway(basket: PaymentServiceBasket) {
  return Boolean(
    basket.paymentGateway &&
      basket.paymentGateway.gateway_type ===
        PAYMENT_GATEWAY_MAP.GATEWAY_STRIPE.id
  );
}

/**
 * Serializes a value safely for logging, handling nested objects and arrays
 * with a depth limit to avoid circular references
 */
function serializeValue(
  value: unknown,
  depth: number,
  maxDepth: number
): unknown {
  // Primitives and null
  if (
    value === null ||
    value === undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  // Stop at max depth to prevent circular references
  if (depth >= maxDepth) {
    return typeof value === "object" ? "[Object]" : String(value);
  }

  // Arrays
  if (Array.isArray(value)) {
    return value.map((item) => serializeValue(item, depth + 1, maxDepth));
  }

  // Objects
  if (typeof value === "object") {
    const serialized: Record<string, unknown> = {};
    const obj = value as Record<string, unknown>;

    // Only serialize own enumerable properties
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const propValue = obj[key];
        // Skip functions and symbols
        if (typeof propValue !== "function" && typeof propValue !== "symbol") {
          serialized[key] = serializeValue(propValue, depth + 1, maxDepth);
        }
      }
    }

    return serialized;
  }

  return String(value);
}

/**
 * Serializes a Stripe error object for safe logging/reporting
 * Extracts all properties while avoiding circular references
 * and non-serializable data that could break error loggers ie. Sentry
 */
export function serializeStripeError(error: unknown): Record<string, unknown> {
  if (!error || typeof error !== "object") {
    return { originalError: String(error) };
  }

  const serialized: Record<string, unknown> = {};
  const errorObj = error as Record<string, unknown>;

  // Properties to skip because they're not serializable or not useful
  const skipProps = new Set(["logger", "stack"]);

  // Serialize all enumerable properties
  // eslint-disable-next-line no-restricted-syntax
  for (const key in errorObj) {
    if (Object.prototype.hasOwnProperty.call(errorObj, key)) {
      const value = errorObj[key];

      // Skip non-serializable properties, functions, and symbols
      if (
        !skipProps.has(key) &&
        typeof value !== "function" &&
        typeof value !== "symbol"
      ) {
        // Use recursive serialization with depth limit of 4 to handle nested structures
        serialized[key] = serializeValue(value, 0, 4);
      }
    }
  }

  return serialized;
}
